import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API, LOGO_URL } from "@/lib/api";
import axios from "axios";
import { StatusBadge } from "@/components/StatusBadge";
import { Truck, MapPin, Search, Loader2, PackageX, ArrowLeft } from "lucide-react";

const DOT = { pending: "bg-amber-500", in_transit: "bg-blue-500", delivered: "bg-emerald-500", exception: "bg-red-500" };

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}

export default function Track() {
  const { trackingNumber } = useParams();
  const nav = useNavigate();
  const [code, setCode] = useState(trackingNumber || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTrack = async (tn) => {
    if (!tn) return;
    setLoading(true); setError(""); setData(null);
    try {
      const { data } = await axios.get(`${API}/track/${tn.toUpperCase()}`);
      setData(data);
    } catch {
      setError("No encontramos ningún envío con ese número.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (trackingNumber) fetchTrack(trackingNumber); }, [trackingNumber]);

  const submit = (e) => { e.preventDefault(); if (code.trim()) nav(`/track/${code.trim().toUpperCase()}`); };
  const events = data ? [...(data.tracking_events || [])].reverse() : [];

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-[#0A0A0A] h-16 flex items-center px-4 md:px-8 justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Triple M" className="w-10 h-10 bg-white p-0.5 object-contain" />
          <div>
            <div className="font-display font-black text-white text-lg tracking-tighter leading-none">TRIPLE M</div>
            <div className="font-mono text-[8px] tracking-[0.2em] text-freight">SERVICIOS LOGÍSTICOS</div>
          </div>
        </Link>
        <Link to="/login" data-testid="go-login" className="text-neutral-300 hover:text-freight text-sm font-bold uppercase transition-colors">Acceder</Link>
      </header>

      <div className="max-w-2xl mx-auto p-4 md:p-8">
        <button onClick={() => nav("/login")} className="flex items-center gap-2 text-sm font-bold uppercase text-muted-foreground hover:text-freight mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Inicio
        </button>

        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-6">Rastrear Envío</h1>

        <form onSubmit={submit} className="flex gap-2 mb-8">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="track-page-input" className="w-full bg-card border border-input rounded-none pl-9 pr-3 py-3 font-mono focus:outline-none focus:ring-2 focus:ring-freight"
              placeholder="TM-XXXXXX" value={code} onChange={(e) => setCode(e.target.value)} />
          </div>
          <button data-testid="track-page-btn" type="submit" className="bg-freight hover:bg-freight-hover text-black font-bold uppercase px-6 transition-colors">Buscar</button>
        </form>

        {loading && <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-freight" /></div>}

        {error && (
          <div className="border border-dashed border-border p-12 text-center" data-testid="track-error">
            <PackageX className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">{error}</p>
          </div>
        )}

        {data && (
          <div className="animate-slide-up" data-testid="track-result">
            <div className="bg-card border border-border p-6 mb-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-mono text-xl font-bold text-freight">{data.tracking_number}</div>
                  <div className="text-muted-foreground text-sm mt-1">{data.description}</div>
                </div>
                <StatusBadge status={data.status} />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-border text-sm">
                <div><div className="text-[10px] font-bold uppercase text-muted-foreground">Origen</div><div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-freight" />{data.origin}</div></div>
                <div><div className="text-[10px] font-bold uppercase text-muted-foreground">Destino</div><div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-freight" />{data.destination}</div></div>
              </div>
            </div>

            <div className="bg-card border border-border p-6">
              <h2 className="font-display text-lg font-black uppercase tracking-tight mb-4">Historial</h2>
              <ol className="relative">
                {events.map((e, i) => (
                  <li key={i} className="relative pl-8 pb-6 last:pb-0">
                    {i !== events.length - 1 && <span className="absolute left-[7px] top-4 bottom-0 w-px bg-border" />}
                    <span className={`absolute left-0 top-1 w-4 h-4 rounded-full border-2 border-card ${DOT[e.status] || "bg-neutral-400"}`} />
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={e.status} />
                      <span className="font-mono text-xs text-muted-foreground">{fmt(e.timestamp)}</span>
                    </div>
                    {e.location && <div className="text-sm font-medium mt-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-freight" />{e.location}</div>}
                    {e.note && <div className="text-sm text-muted-foreground mt-0.5">{e.note}</div>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
